// =============================================================================
// app/src/componentes/GraficoFluxo.tsx — o gráfico do fluxo de caixa.
//
// SVG à mão, como tudo que desenha neste app (código de barras, mapa da
// loja): não há biblioteca de gráficos no bundle e a CSP não deixa entrar
// script externo — decisão antiga e certa para um sistema de balcão em 4G.
//
// AS DECISÕES DE DESENHO, e por quê:
//  * barras VERTICAIS: série temporal se lê da esquerda para a direita; as
//    barras horizontais das outras telas são listas por categoria — outro
//    problema;
//  * entradas para CIMA (verde), saídas para BAIXO (vermelho) da linha do
//    zero — o sinal do dinheiro vira posição, sem legenda para decorar;
//  * PASSADO sólido, PREVISTO com opacidade 0.45: a posição no tempo já
//    separa os dois, o tom só reforça; a legenda desambigua;
//  * a linha branca é a VARIAÇÃO ACUMULADA — nunca "saldo": o sistema não
//    conhece o saldo bancário da loja, e rotular errado faria o dono ler o
//    gráfico como extrato;
//  * toque = destaque + painel de detalhe ABAIXO (não tooltip flutuante:
//    tooltip briga com o dedo e estoura da tela no celular; painel fixo é o
//    padrão da casa);
//  * o vermelho DA MARCA não entra (regra 10): verde/vermelho aqui significam
//    entrada/saída, como no resto do dinheiro.
// =============================================================================
import { useState } from 'react';
import { abreviarBRL, escalaDoGrafico, type PontoDoGrafico } from '../lib/fluxo';
import { brl } from '../lib/formato';

const LARGURA = 720;
const ALTURA = 260;
const MARGEM_ESQ = 46;   // onde vivem os rótulos do eixo Y
const MARGEM_INF = 18;   // onde vivem as datas
const MEIO = (ALTURA - MARGEM_INF) / 2;

/**
 * `escala` diz se cada barra é um DIA ou uma SEMANA, e vem de fora porque o
 * componente não tem como saber: `pontos` já está agregado, e contar pontos
 * mede a grandeza errada. Antes isto era `pontos.length > 45` aqui dentro — e
 * como depois de agregar nunca se passa de ~58, os dois modos da tela chamavam
 * "Dia" o que era semana.
 */
export default function GraficoFluxo(
  { pontos, escala = 'dia' }: { pontos: PontoDoGrafico[]; escala?: 'dia' | 'semana' },
) {
  const [ativo, setAtivo] = useState<number | null>(null);

  if (pontos.length === 0) return null;
  const { teto, ticks } = escalaDoGrafico(pontos);
  const areaLargura = LARGURA - MARGEM_ESQ;
  const passo = areaLargura / pontos.length;
  const larguraBarra = Math.max(2, Math.min(18, passo * 0.36));
  // Altura útil de cada metade (entradas em cima, saídas embaixo).
  const alturaMeia = MEIO - 8;
  const y = (v: number) => (v / teto) * alturaMeia;

  // A cada quantos pontos sai um rótulo de data — ~6 rótulos no total.
  const cadaRotulo = Math.max(1, Math.ceil(pontos.length / 6));

  const linhaAcumulado = pontos
    .map((p, i) => {
      const x = MARGEM_ESQ + i * passo + passo / 2;
      return `${x},${MEIO - y(p.acumulado)}`;
    })
    .join(' ');

  const detalhe = ativo === null ? null : pontos[ativo]!;

  return (
    <div>
      <svg viewBox={`0 0 ${LARGURA} ${ALTURA}`} className="w-full" role="img"
           aria-label="Gráfico de fluxo de caixa: entradas para cima, saídas para baixo, e a linha da variação acumulada">
        {/* Grade e eixo Y: o teto, a metade e os espelhos negativos. */}
        {[...ticks.map((t) => ({ v: t, yy: MEIO - y(t) })),
          { v: 0, yy: MEIO },
          ...ticks.map((t) => ({ v: -t, yy: MEIO + y(t) }))].map(({ v, yy }) => (
          <g key={v}>
            <line x1={MARGEM_ESQ} x2={LARGURA} y1={yy} y2={yy}
                  stroke="var(--color-edge)" strokeWidth={v === 0 ? 1.5 : 0.5} />
            <text x={MARGEM_ESQ - 4} y={yy + 3} textAnchor="end" fontSize="9"
                  fill="var(--color-dim)">
              {abreviarBRL(v)}
            </text>
          </g>
        ))}

        {/* As barras: entradas para cima, saídas para baixo. A fatia PREVISTA
            sai apagada em cima da realizada — passado sólido, futuro em tom. */}
        {pontos.map((p, i) => {
          const x = MARGEM_ESQ + i * passo + (passo - larguraBarra) / 2;
          const entReal = p.entradas - p.entradasPrevistas;
          const saiReal = p.saidas - p.saidasPrevistas;
          return (
            <g key={p.dia}>
              {entReal > 0 && (
                <rect x={x} y={MEIO - y(entReal)} width={larguraBarra} height={y(entReal)}
                      fill="var(--color-ok)" />
              )}
              {p.entradasPrevistas > 0 && (
                <rect x={x} y={MEIO - y(p.entradas)} width={larguraBarra}
                      height={y(p.entradasPrevistas)} fill="var(--color-ok)" opacity="0.45" />
              )}
              {saiReal > 0 && (
                <rect x={x} y={MEIO} width={larguraBarra} height={y(saiReal)}
                      fill="var(--color-erro)" />
              )}
              {p.saidasPrevistas > 0 && (
                <rect x={x} y={MEIO + y(saiReal)} width={larguraBarra}
                      height={y(p.saidasPrevistas)} fill="var(--color-erro)" opacity="0.45" />
              )}
              {/* O alvo do toque: coluna inteira, invisível. */}
              <rect x={MARGEM_ESQ + i * passo} y={0} width={passo} height={ALTURA - MARGEM_INF}
                    fill="transparent" style={{ cursor: 'pointer' }}
                    onClick={() => setAtivo(ativo === i ? null : i)} />
              {ativo === i && (
                <rect x={MARGEM_ESQ + i * passo} y={0} width={passo}
                      height={ALTURA - MARGEM_INF} fill="var(--color-paper)" opacity="0.08" />
              )}
              {i % cadaRotulo === 0 && (
                <text x={MARGEM_ESQ + i * passo + passo / 2} y={ALTURA - 5}
                      textAnchor="middle" fontSize="9" fill="var(--color-dim)">
                  {p.rotulo}
                </text>
              )}
            </g>
          );
        })}

        {/* A variação acumulada por cima de tudo. */}
        <polyline points={linhaAcumulado} fill="none"
                  stroke="var(--color-paper)" strokeWidth="1.5" />
      </svg>

      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.6875rem] text-dim">
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-2.5 bg-ok" /> entrou
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-2.5 bg-erro" /> saiu
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-2.5 bg-ok opacity-45" /> previsto
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-4 bg-paper" /> variação acumulada na janela
        </span>
      </div>

      {/* O detalhe do toque: painel fixo abaixo, no padrão da casa. */}
      {detalhe && (
        <div className="cartao mt-2 grid grid-cols-2 gap-2 p-3 text-sm sm:grid-cols-4">
          <div>
            <p className="rotulo">{escala === 'semana' ? 'Semana de' : 'Dia'}</p>
            <p className="font-bold">{detalhe.rotulo}{detalhe.passado ? '' : ' · previsto'}</p>
          </div>
          <div>
            <p className="rotulo">Entra</p>
            <p className="font-mono font-bold tabular-nums text-ok">{brl(detalhe.entradas)}</p>
          </div>
          <div>
            <p className="rotulo">Sai</p>
            <p className="font-mono font-bold tabular-nums text-erro">{brl(detalhe.saidas)}</p>
          </div>
          <div>
            <p className="rotulo">Acumulado</p>
            <p className="font-mono font-bold tabular-nums">{brl(detalhe.acumulado)}</p>
          </div>
          {detalhe.semValor > 0 && (
            <p className="col-span-2 text-xs text-alerta sm:col-span-4">
              +{detalhe.semValor} conta{detalhe.semValor > 1 ? 's' : ''} de valor
              variável fora da soma.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
